/**
 * Projektmappe: Fotos ohne Projekt (Eingang + Stapel „Zuordnen") und Scans/Entwürfe
 * am Projekt. Server: `hetzner/webhook/kontor_medien.py`, Konzept im Gehirn
 * (kontor-app/dokumente/Konzept - Projektmappe mit Fotos, Scans und Entwürfen.md).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, apiGet, apiPatch, apiPost, fehlerText } from "@vimaana/ui";

export type EingangAbgelehnt = { datei: string; art: "doppelt" | "ohne_ort" | "fehler"; grund: string };
/** `ids`: die neuen Aufnahmen am Projekt — für die Vorschau im Bericht. */
export type EingangZugeordnet = { projekt_id: string; titel: string; anzahl: number; ids: string[] };
export type EingangErgebnis = {
  /** Seit 13.10.2026 immer leer — zugeordnet wird nur noch mit Bestätigung. */
  zugeordnet: EingangZugeordnet[];
  /** Erkannte Projekte: nur Vorschläge, im Stapel vorausgewählt. */
  erkannt: EingangZugeordnet[];
  zuordnen: number;
  abgelehnt: EingangAbgelehnt[];
};

export type StapelVorschlag = { projekt_id: string; titel: string; status: string; meter: number; im_zeitraum: boolean };
export type StapelFoto = { id: string; aufgenommen_am: string; breite: number | null; laenge: number | null };
export type StapelBuendel = {
  tag: string;
  anzahl: number;
  ids: string[];
  breite: number | null;
  laenge: number | null;
  von: string[];
  vorschlaege: StapelVorschlag[];
  fotos: StapelFoto[];
  /** Das Projekt, das Kontor erkannt hat — vorausgewählt, bestätigt wird von Hand. */
  erkannt: { projekt_id: string; titel: string } | null;
};
export type Stapel = { anzahl: number; buendel: StapelBuendel[] };

/** Jedes Projekt, gleich welcher Status — Karte und Suche beim Zuordnen. */
export type ZuordnenProjekt = {
  projekt_id: string;
  titel: string;
  status: string;
  kunde: string;
  adresse: string;
  breite: number | null;
  laenge: number | null;
  /** Termintage (YYYY-MM-DD), daraus der Phasenvorschlag. */
  tage: string[];
};
export type FotoPhase = "bestandsaufnahme" | "verlauf" | "ergebnis" | "sonstiges";

export type UebersichtFoto = {
  id: string;
  aufgenommen_am: string;
  phase: string;
  arbeitstag: string;
  quelle: string;
  breite: number | null;
  laenge: number | null;
};
export type UebersichtProjekt = {
  projekt_id: string;
  titel: string;
  status: string;
  zuletzt: string;
  anzahl: number;
  fotos: UebersichtFoto[];
};

export type MediumArt = "scan" | "sketchup" | "rendering" | "film" | "plan";
export type MediumDatei = { teil: "sketchup" | "twinmotion" | "steckbrief"; name: string; url: string };
/** Bestandsmodell aus einem Scan — Claude baut es auf dem Evo (kontor_bestandsmodell.py). */
export type ModellZustand = "" | "wartet" | "arbeitet" | "fertig" | "rueckfrage" | "fehler";
export type ModellDatei = { teil: "bauset" | "lageplan" | "bericht"; name: string; url: string };
export type ModellEintrag = { wer: "mensch" | "claude" | "system"; text: string; zeit: string; von: string };
export type ModellStand = {
  zustand: ModellZustand;
  hinweis: string;
  titel: string;
  laeufe: number;
  /** Zur letzten fertigen Fassung liegt schon eine .skp am Projekt */
  in_sketchup: boolean;
  dateien: ModellDatei[];
  verlauf: ModellEintrag[];
};
export type Medium = {
  id: string;
  art: MediumArt;
  titel: string;
  dateiname: string;
  groesse: number;
  erstellt_von: string;
  created_at: string;
  fuer_kunde: boolean;
  url: string;
  /** Nur bei Scans: wartet | fertig | fehler */
  zustand: "" | "wartet" | "fertig" | "fehler";
  hinweis: string;
  dateien: MediumDatei[];
  /** Nur bei Scans */
  modell?: ModellStand | null;
};
export type MedienErgebnis = { angelegt: Medium[]; abgelehnt: string[] };

export function useFotoStapel() {
  return useQuery<Stapel>({
    queryKey: ["fotos", "stapel"],
    queryFn: () => apiGet<Stapel>("/api/kontor/fotos/zuordnen"),
    staleTime: 60_000,
  });
}

/** Stand eines Laufs über mehrere Fotos — für die Fortschrittsanzeige. */
export type Schritt = { fertig: number; gesamt: number };

// Jedes Foto wird in der Nextcloud verschoben; 27 Fotos in einer Anfrage hießen
// eine halbe Minute ohne Rückmeldung (13.10.2026). In Portionen gesendet, sieht man,
// wie es vorangeht — und bricht etwas ab, ist alles davor schon erledigt.
const PORTION = 4;

async function inPortionen(
  ids: string[], senden: (teil: string[]) => Promise<number>, fortschritt?: (s: Schritt) => void,
): Promise<number> {
  let erledigt = 0;
  fortschritt?.({ fertig: 0, gesamt: ids.length });
  for (let i = 0; i < ids.length; i += PORTION) {
    const teil = ids.slice(i, i + PORTION);
    erledigt += await senden(teil);
    fortschritt?.({ fertig: Math.min(ids.length, i + teil.length), gesamt: ids.length });
  }
  return erledigt;
}

export function useStapelZuordnen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ fortschritt, ...v }: {
      ids: string[]; projekt_id: string; phase?: FotoPhase; umhaengen?: boolean; fortschritt?: (s: Schritt) => void;
    }) => ({
      zugeordnet: await inPortionen(
        v.ids,
        (teil) => apiPost<{ zugeordnet: number }>("/api/kontor/fotos/zuordnen", { ...v, ids: teil }).then((r) => r.zugeordnet),
        fortschritt,
      ),
    }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["fotos", "stapel"] });
      qc.invalidateQueries({ queryKey: ["fotos", "uebersicht"] });
      qc.invalidateQueries({ queryKey: ["projekt"] });
    },
  });
}

export function useZuordnenProjekte() {
  return useQuery<ZuordnenProjekt[]>({
    queryKey: ["fotos", "projekte"],
    queryFn: () => apiGet<ZuordnenProjekt[]>("/api/kontor/fotos/projekte"),
    staleTime: 5 * 60_000,
  });
}

export function useFotoUebersicht(quelle: "eingang" | "alle", tage: number) {
  return useQuery<UebersichtProjekt[]>({
    queryKey: ["fotos", "uebersicht", quelle, tage],
    queryFn: () => apiGet<UebersichtProjekt[]>(`/api/kontor/fotos/uebersicht?quelle=${quelle}&tage=${tage}`),
    staleTime: 30_000,
  });
}

/** Falsch zugeordnete Fotos wieder in den Stapel „Zuordnen“. */
export function useFotosZurueck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, fortschritt }: { ids: string[]; fortschritt?: (s: Schritt) => void }) => ({
      zurueck: await inPortionen(
        ids,
        (teil) => apiPost<{ zurueck: number }>("/api/kontor/fotos/zurueck", { ids: teil }).then((r) => r.zurueck),
        fortschritt,
      ),
    }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["fotos"] });
      qc.invalidateQueries({ queryKey: ["projekt"] });
    },
  });
}

export function useStapelVerwerfen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => apiPost<{ verworfen: number }>("/api/kontor/fotos/verwerfen", { ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fotos", "stapel"] }),
  });
}

export function useMedien(projektId: string) {
  return useQuery<Medium[]>({
    queryKey: ["projekt", projektId, "medien"],
    queryFn: () => apiGet<Medium[]>(`/api/kontor/projekt/${projektId}/medien`),
    staleTime: 30_000,
    // Ein Scan, der noch aufbereitet wird, fragt jede Minute nach — die Werkstatt
    // auf dem Evo schaut alle zehn Minuten in den Eingang.
    // Arbeitet Claude an einem Bestandsmodell, alle 20 Sekunden — der Lauf dauert Minuten.
    refetchInterval: (q) => {
      const liste = q.state.data ?? [];
      if (liste.some((m) => m.modell?.zustand === "wartet" || m.modell?.zustand === "arbeitet")) return 20_000;
      return liste.some((m) => m.zustand === "wartet") ? 60_000 : false;
    },
  });
}

/** Stand beim Hochladen: Datei `nr` von `gesamt`, davon `geladen` von `groesse` Bytes. */
export type Hochladen = { nr: number; gesamt: number; name: string; geladen: number; groesse: number };

/**
 * Hochladen mit Fortschritt in Bytes. `fetch` meldet beim Senden keinen Fortschritt —
 * bei einer SketchUp-Datei von 50 MB sah die App minutenlang aus, als hinge sie (16.10.2026).
 * Fehler wie `apiUpload`: ApiError mit dem Rumpf des Servers, 401 führt zur Anmeldung.
 */
function hochladenMitFortschritt<T>(pfad: string, form: FormData, melde: (geladen: number, gesamt: number) => void): Promise<T> {
  return new Promise<T>((fertig, fehler) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", pfad);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) melde(e.loaded, e.total); };
    xhr.onerror = () => fehler(new ApiError(0, { detail: "Die Verbindung ist abgebrochen — bitte noch einmal versuchen." }, `Netzwerkfehler bei POST ${pfad}`));
    xhr.onload = () => {
      let rumpf: unknown = null;
      if (xhr.responseText) {
        try { rumpf = JSON.parse(xhr.responseText); } catch { rumpf = xhr.responseText; }
      }
      if (xhr.status >= 200 && xhr.status < 300) { fertig(rumpf as T); return; }
      if (xhr.status === 401 && window.location.pathname !== "/login") window.location.replace("/login");
      if (xhr.status === 413) rumpf = { detail: "Die Datei ist zu groß für den Server (höchstens 2 GB)." };
      fehler(new ApiError(xhr.status, rumpf, `HTTP ${xhr.status} on POST ${pfad}`));
    };
    xhr.send(form);
  });
}

export function useMedienAblegen(projektId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ dateien, fortschritt }: { dateien: File[]; fortschritt?: (s: Hochladen) => void }) => {
      // Eine Datei je Anfrage: ein Scan kann einige hundert MB groß sein.
      const gesamt: MedienErgebnis = { angelegt: [], abgelehnt: [] };
      for (let i = 0; i < dateien.length; i += 1) {
        const d = dateien[i];
        const form = new FormData();
        form.append("datei", d);
        fortschritt?.({ nr: i + 1, gesamt: dateien.length, name: d.name, geladen: 0, groesse: d.size });
        try {
          const teil = await hochladenMitFortschritt<MedienErgebnis>(`/api/kontor/projekt/${projektId}/medien`, form,
            (geladen, groesse) => fortschritt?.({ nr: i + 1, gesamt: dateien.length, name: d.name, geladen, groesse }));
          gesamt.angelegt.push(...teil.angelegt);
          gesamt.abgelehnt.push(...teil.abgelehnt);
        } catch (e) {
          gesamt.abgelehnt.push(fehlerText(e, `${d.name}: Hochladen fehlgeschlagen`));
        }
      }
      return gesamt;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["projekt", projektId, "medien"] }),
  });
}

export function useMediumAendern(projektId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; titel?: string; fuer_kunde?: boolean; verwerfen?: boolean }) =>
      apiPatch<{ ok: boolean }>(`/api/kontor/projekt/${projektId}/medien/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["projekt", projektId, "medien"] }),
  });
}

/** Bestandsmodell starten oder Claude mit einer Korrektur/Antwort weiterarbeiten lassen. */
export function useBestandsmodell(projektId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) =>
      apiPost<ModellStand>(`/api/kontor/projekt/${projektId}/medien/${id}/bestandsmodell`, { text }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["projekt", projektId, "medien"] }),
  });
}

/** Zugangsschlüssel für die SketchUp-Erweiterung (kontor_sketchup.py, Migration 219). */
export type SketchupSchluessel = { id: string; name: string; angelegt_am: string; zuletzt_am: string; widerrufen: boolean };

export function useSketchupSchluessel(aktiv: boolean) {
  return useQuery<SketchupSchluessel[]>({
    queryKey: ["sketchup", "schluessel"],
    queryFn: () => apiGet<SketchupSchluessel[]>("/api/kontor/sketchup/schluessel"),
    enabled: aktiv,
  });
}

export function useSchluesselAnlegen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => apiPost<SketchupSchluessel & { schluessel: string }>("/api/kontor/sketchup/schluessel", { name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sketchup", "schluessel"] }),
  });
}

export function useSchluesselWiderrufen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<{ ok: boolean }>(`/api/kontor/sketchup/schluessel/${id}/widerrufen`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sketchup", "schluessel"] }),
  });
}
